import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export interface RowMenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

/** A "⋮" button that opens a small action menu. The menu is `position: fixed`
 *  (anchored to the button's rect) so it escapes the table card's
 *  `overflow-x: auto` clipping. Closes on outside click, Escape, scroll or resize. */
export function RowMenu({
  items,
  label = 'More actions',
}: {
  items: (RowMenuItem | null | false)[];
  label?: string;
}): ReactNode {
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const visible = items.filter((i): i is RowMenuItem => Boolean(i));

  // Opens upward instead when the menu would run off the bottom of the screen.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    const button = buttonRef.current;
    if (!pos || !menu || !button) return;
    const height = menu.offsetHeight;
    const r = button.getBoundingClientRect();
    if (pos.top + height > window.innerHeight - 8 && r.top - height - 4 >= 8) {
      setPos({ top: r.top - height - 4, right: pos.right });
    }
  }, [pos]);

  useEffect(() => {
    if (!pos) return;
    const close = (): void => setPos(null);
    const onPointer = (e: PointerEvent): void => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || buttonRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        close();
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [pos]);

  function toggle(): void {
    if (pos) {
      setPos(null);
      return;
    }
    const r = buttonRef.current!.getBoundingClientRect();
    setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="btn small ghost row-menu-trigger"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={pos !== null}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
      >
        ⋮
      </button>
      {pos ? (
        <div
          ref={menuRef}
          role="menu"
          className="row-menu"
          style={{ top: pos.top, right: pos.right }}
          onClick={(e) => e.stopPropagation()}
        >
          {visible.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={item.danger ? 'danger' : undefined}
              disabled={item.disabled}
              onClick={() => {
                setPos(null);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}
